import React, { FC, useState, useRef, useEffect } from 'react'
import { IPriceComparisonCarousel, ISlideState } from '../types'
import { useSwipeable } from 'react-swipeable'

// MUI
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Container from '@mui/material/Container'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import Grid from '@mui/material/Grid'
import { boxShadow } from '@tdcerhverv/mui-theme/dist/theme/global/shadows'

// Icons
import Arrow_left from '@tdcerhverv/parrotfish/dist/icons/Icons/Arrow_left.svg'
import Arrow_right from '@tdcerhverv/parrotfish/dist/icons/Icons/Arrow_right.svg'

const PriceComparisonCarousel: FC<IPriceComparisonCarousel> = ({ children, render, ...props }) => {
  const { palette } = useTheme()
  const { length } = children
  const [windowSize, setWindowSize] = useState<number | null>(null)
  const [slideElementWidth, setSlideElementWidth] = useState<number>(0)
  const [slideState, setSlideState] = useState<ISlideState>({
    slidePosition: 0,
    activeSlide: 0,
  })
  const styles = {
    root: {
      overflow: 'hidden',
      position: 'relative',
      padding: '0 57px',
      '@media (max-width: 420px)': {
        padding: '0',
      },
    },
    carouselArrow: {
      position: 'absolute',
      top: '50%',
      height: '130px',
      width: '47px',
      backgroundColor: palette.primary.dark,
      color: palette.common.white,
      padding: '16px',
      borderRadius: '6px',
      justifyContent: 'center',
      alignItems: 'center',
      boxShadow: '0px 5px 8px 0px rgba(26, 26, 26, 0.14), 0px 1px 14px 0px rgba(26, 26, 26, 0.12)',

      '&:hover': {
        backgroundColor: palette.primary.dark,
        color: palette.common.white,
      },
      '&.Mui-disabled': {
        boxShadow: 'none',
      },
      '@media (max-width: 420px)': {
        display: 'none',
      },
    },
    prevArrow: {
      left: '0px',
    },
    nextArrow: {
      right: '0px',
    },

    container: {
      padding: '0 !important',
      display: 'grid',
    },
    slider: {
      width: `100%`,
      overflow: 'hidden',
      paddingTop: '33px',
    },
    sliderContainer: {
      display: 'grid',
      gridTemplateRows: 'none',
      gridAutoFlow: 'column',
      margin: 0,
      padding: 0,
      transition: `transform 300ms ease`,
      transform: `translateX(-${slideState?.slidePosition}px)`,
      gridAutoColumns: `calc(100%  / ${props.numberOfSlidesInView})`,
    },

    controlsContainer: {
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    bulletContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
    sliderBullet: {
      position: 'relative',
      padding: '15px',
      borderRadius: '50px',
      cursor: 'pointer',
      transition: 'all 300ms ease',
      '& span': {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: `translate(-50%, -50%)`,
        display: 'block',
        backgroundColor: palette.common.white,
        width: 6,
        height: 6,
        borderRadius: 50,
        transition: 'all 300ms ease',
      },
      '&:first-child': {
        marginLeft: 0,
      },
      '&:last-child': {
        marginRight: 0,
      },
      '&:hover': {
        backgroundColor: palette.grey[200],
        '& span': {
          backgroundColor: palette.primary.light,
          width: 8,
          height: 8,
        },
      },
    },
    activeBullet: {
      '& span': {
        backgroundColor: palette.primary.light,
        width: '8px',
        height: '8px',
      },
    },
    tableRoot: {
      color: palette.common.black,
      boxShadow: boxShadow.small,
      backgroundColor: palette.common.white,
      borderSpacing: 0,
      width: '100%',
      '& .MuiGrid-item': {
        padding: 0,
      },
    },
  }

  const ref = useRef<HTMLTableRowElement>(null)

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    handleResize()

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (!ref.current?.children[0]) return
    setSlideState({
      slidePosition: 0,
      activeSlide: 0,
    })
    setSlideElementWidth(
      ref.current.children[0].getBoundingClientRect().width, // Getting  the exact width of the slide element within the carousel
    )
  }, [windowSize, props.numberOfSlidesInView, ref])
  const slideLength = props.numberOfSlidesInView

  const getAllSlideLength = () => {
    return slideElementWidth * (length - slideLength)
  }

  const nextSlide = () => {
    slideState.slidePosition < getAllSlideLength() &&
      setSlideState({
        slidePosition: slideState.slidePosition + slideElementWidth,
        activeSlide: slideState.activeSlide + 1,
      })
  }
  const previousSlide = () => {
    slideState.slidePosition > 0 &&
      setSlideState({
        slidePosition: slideState.slidePosition - slideElementWidth,
        activeSlide: slideState.activeSlide - 1,
      })
  }

  const setSlide = (value: number) => {
    setSlideState({
      slidePosition: slideElementWidth * value,
      activeSlide: value,
    })
  }
  const swipeHandler = useSwipeable({
    onSwipedLeft: () => nextSlide(),
    onSwipedRight: () => previousSlide(),
    delta: 10,
    trackMouse: false,
  })

  const Bullets = (): JSX.Element => {
    const bulletNo = length - slideLength + 1
    return (
      <Box sx={styles.bulletContainer}>
        {Array.from(Array(bulletNo)).map((_bullet, i) => {
          return (
            <Box
              key={i}
              id={`carousel-bullet-${i}`}
              role="button"
              aria-label={`Knap til slide ${i + 1}`}
              sx={[styles.sliderBullet, slideState.activeSlide === i ? styles.activeBullet : {}]}
              onClick={() => setSlide(i)}
            >
              <span></span>
            </Box>
          )
        })}
      </Box>
    )
  }

  return (
    <Box aria-roledescription="pricecomparisoncarousel" aria-label={props.title} sx={styles.root} {...swipeHandler}>
      <Container sx={styles.container}>
        <Box sx={styles.slider}>
          <table style={{ ...styles.tableRoot }}>
            <tbody>
              <Grid ref={ref} component="tr" sx={styles.sliderContainer} container alignItems="stretch">
                {children}
              </Grid>

              {render && render()}
            </tbody>
          </table>
        </Box>

        <Box sx={styles.controlsContainer}>
          {length > slideLength && (
            <Box display="flex" justifyContent={'center'} flex="1">
              <IconButton
                id="carousel-previous-slide"
                sx={{ ...styles.prevArrow, ...styles.carouselArrow, marginRight: '8px' }}
                onClick={previousSlide}
                disabled={slideState.slidePosition <= 0}
              >
                <SvgIcon
                  component={Arrow_left}
                  viewBox="0 0 48 48"
                  sx={{ color: slideState.slidePosition <= 0 ? palette.grey[400] : 'inherit' }}
                />
              </IconButton>
              <IconButton
                id="carousel-next-slide"
                sx={{ ...styles.nextArrow, ...styles.carouselArrow, marginLeft: '8px' }}
                onClick={nextSlide}
                disabled={slideState.slidePosition >= getAllSlideLength()}
              >
                <SvgIcon
                  component={Arrow_right}
                  viewBox="0 0 48 48"
                  sx={{ color: slideState.slidePosition >= getAllSlideLength() ? palette.grey[400] : 'inherit' }}
                />
              </IconButton>
            </Box>
          )}
        </Box>
        {length > slideLength && (
          <Box sx={styles.controlsContainer}>
            <Box display="flex">
              <Bullets />
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  )
}

export default PriceComparisonCarousel
